<template>
    <OverlayLoader :show="loadingPreferences" />
    <CardLoader v-if="loading" />
    <div class="w-100" v-else>
        <div class="row g-3">
            <div class="col-md-6">
                <material-input id="user-id" name="id" label="ID" type="number" v-model="user.id" disabled />
            </div>
            <div class="col-md-6">
                <material-input id="user-first-name" name="firstName" label="First Name" v-model="user.firstName" required :disabled="!canEditProfile" />
            </div>
            <div class="col-md-6">
                <material-input id="user-middle-name" name="middleName" label="Middle Name" v-model="user.middleName" :disabled="!canEditProfile" />
            </div>
            <div class="col-md-6">
                <material-input id="user-last-name" name="lastName" label="Last Name" v-model="user.lastName" required :disabled="!canEditProfile" />
            </div>
            <div class="col-md-6">
                <material-input id="user-email" name="email" label="Email" v-model="user.email" 
                    :disabled="!canEditProfile" :error="!!emailError" @blur="validateEmail" />
                <small v-if="emailError" class="text-danger">{{ emailError }}</small>
            </div>
            <div class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Use Proxy Email</label>
                    <HelpTextTooltip helpKey="person_detail_proxyemail" />
                </div>
                <MaterialSelect name="useProxyEmail" v-model="user.useProxyEmail"
                    :options="yesNoOptions" :disabled="!canEditProfile" />
            </div>
            <div class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Proxy Email</label>
                    <HelpTextTooltip helpKey="person_detail_proxyemail" />
                </div>
                <material-input id="user-proxy-email" name="proxyEmail" v-model="user.proxyEmail"
                    :disabled="!canEditProfile || !user.useProxyEmail" :error="!!proxyEmailError" @blur="validateProxyEmail" />
                <small v-if="proxyEmailError" class="text-danger">{{ proxyEmailError }}</small>
            </div>
            <div class="col-md-12" v-if="!canEditProfile && profilePermission.reason">
                <div class="d-flex align-items-center gap-2 text-warning">
                    <i class="material-icons" style="font-size: 16px;">lock</i>
                    <small>{{ profilePermission.reason }}</small>
                </div>
            </div>
            <div class="col-md-6" v-if="canUpdatePassword">
                <material-input id="user-password" name="password" label="Password" v-model="user.password"
                    type="password" placeholder="Enter new password to update" />
            </div>
            <div class="col-md-6" v-else-if="passwordPermission.reason">
                <label class="form-label mb-0 text-muted">Password</label>
                <div class="d-flex align-items-center gap-2 text-warning mt-1">
                    <i class="material-icons" style="font-size: 16px;">lock</i>
                    <small>{{ passwordPermission.reason }}</small>
                </div>
            </div>
            <div class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Confirmed</label>
                    <HelpTextTooltip helpKey="person_detail_confirmed" />
                </div>
                <MaterialSelect id="confirmed" name="confirmed" v-model="user.confirmed" :options="yesNoOptions"
                    :disabled="!isSuperAdmin || user.confirmed === true || user.confirmed === 1" />
            </div>
            <div class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Registration Type</label>
                    <HelpTextTooltip helpKey="person_detail_regtype" />
                </div>
                <MaterialSelect id="registrationtype" name="registrationType" v-model="user.registrationType"
                    :options="registrationTypes" :disabled="!isSuperAdmin" />
            </div>
            <div class="col-md-6">
                <MaterialSelect name="country" label="Country" v-model="user.country" :options="countryOptions" />
            </div>
            <div class="col-md-6">
                <MaterialSelect name="timezone" label="TimeZone" v-model="user.timezone" :options="timezoneOptions" />
            </div>
            <div class="col-md-6">
                <material-input id="user-mobile" name="mobile" label="Mobile" v-model="user.mobile" />
            </div>
            <div class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Enable SMS/Text alerts</label>
                    <HelpTextTooltip helpKey="person_detail_smsalerts" />
                </div>
                <MaterialSelect name="smsAlerts" v-model="user.smsAlerts"
                    :options="yesNoOptions" />
            </div>
            <div class="col-md-6">
                <MaterialSelect name="gender" label="Gender" v-model="user.gender" :options="genderOptions" />
            </div>
            <div class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Last Email Date</label>
                    <HelpTextTooltip helpKey="person_detail_lastemaildate" />
                    <button v-if="canResetReminderDate" type="button"
                        class="btn btn-sm btn-outline-secondary m-0 p-1"
                        :disabled="resettingReminderDate"
                        title="Reset volunteer reminder date"
                        aria-label="Reset volunteer reminder date"
                        @click="resetPersonReminderDate">
                        <span v-if="resettingReminderDate" class="spinner-border spinner-border-sm"></span>
                        <i v-else class="material-icons text-sm">restart_alt</i>
                    </button>
                </div>
                <material-input id="user-last-email-date" name="lastEmailDate"
                    v-model="user.lastEmailDate" disabled />
            </div>
            <div class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Last Sign-up Email Date</label>
                    <HelpTextTooltip helpKey="person_detail_lastsignupdate" />
                </div>
                <material-input id="user-last-signup-email-date" name="lastSignupEmailDate"
                    v-model="user.lastSignupEmailDate" disabled />
            </div>
            <div class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Welcome Email Sent</label>
                </div>
                <span v-if="user.sentWelcomeEmail" class="badge bg-success d-inline-flex align-items-center gap-1 px-3 py-2">
                    <i class="material-icons" style="font-size:16px;">check_circle</i> Yes
                </span>
                <span v-else class="badge bg-secondary d-inline-flex align-items-center gap-1 px-3 py-2">
                    <i class="material-icons" style="font-size:16px;">cancel</i> No
                </span>
            </div>
            <div class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Last Logon Date</label>
                    <HelpTextTooltip helpKey="person_detail_lastlogondate" />
                </div>
                <material-input id="user-last-logon-date" name="lastLogonDate"
                    v-model="user.lastLogonDate" disabled />
            </div>
            <div class="col-md-6" v-if="user.icalUrl">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">iCal URL</label>
                    <HelpTextTooltip helpKey="person_detail_icalurl" />
                </div>
                <material-input id="user-ical-url" name="icalUrl" v-model="user.icalUrl" disabled>
                    <template #append>
                        <button
                            class="btn btn-sm border border-1 bg-transparent px-2 m-0 d-flex align-items-center justify-content-center"
                            type="button" @click="copyIcalUrl" :disabled="!user.icalUrl" title="Copy iCal URL">
                            <i class="material-icons text-sm">content_copy</i>
                        </button>
                    </template>
                </material-input>
            </div>
            <div class="col-md-6 d-flex align-items-end gap-2">
                <MaterialButton variant="text" color="info" @click="openModal">
                    View Scheduling Preferences Summary
                </MaterialButton>
                <!-- <MaterialButton v-if="user.assignments" variant="text" color="primary" @click="openAssignmentsModal">
                    View Assignments
                </MaterialButton> -->
            </div>
        </div>
        <!-- Save Button -->
        <div class="d-flex justify-content-end mt-4">
            <!-- <MaterialButton color="info" ">Save</MaterialButton> -->
            <MaterialButton @click="saveUser" variant="primary" :class="['px-4', hasChanges && !isSaving ? 'pulse-glow' : '']" :disabled="isSaving">
                <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status"
                    aria-hidden="true"></span>
                <i v-else class="material-icons text-lg me-1">save</i>
                {{ isSaving ? 'Saving...' : 'Save' }}
            </MaterialButton>
        </div>
        <AccountDeletion
            v-if="props.selfProfile && personId && locationId"
            class="mt-5"
            :key="`${personId}:${locationId}`"
            :person-id="personId"
            :person-name="profileDisplayName"
            :location-id="locationId"
            :self-service="true"
            @completed="handleSelfAccountActionCompleted"
        />
    </div>

    <!-- Preview Modal -->
    <!-- <ContentModal v-model="showModal" title="User Preferences">
        <template #default>
            <div v-if="personPreferences">

                <div class="d-flex gap-8">
                    <span class="d-flex gap-2">
                        <h6 class="mb-0">Name:</h6>
                        <p class="mb-0">{{ personPreferences.user_info?.name }}</p>
                    </span>
                    <span class="d-flex gap-2">
                        <h6 class="mb-0">Email:</h6>
                        <p class="mb-0">{{ personPreferences.user_info?.email }}</p>
                    </span>
                </div>

                <h6 class="mt-4">Summary:</h6>
                <ul v-if="personPreferences.summary?.length" class="mb-0">
                    <li v-for="(sentence, i) in personPreferences.summary" :key="'summary'+i">{{ sentence }}</li>
                </ul>
                <div v-else class="text-muted">No preference summary available.</div>

                <h6 class="mt-4">Skills:</h6>
                <div v-if="!personPreferences.skills || personPreferences.skills.length === 0">
                    No skills.
                </div>
                <MaterialSelectedBadge v-else v-for="skill in personPreferences.skills" :key="skill.id"
                    :label="`${skill.name} (Level ${skill.level})`" color="info" />

                <h6 class="mt-4">Interests:</h6>
                <div class="d-flex flex-wrap">
                    <span v-if="!personPreferences.interest_skills || personPreferences.interest_skills.length === 0">
                        No interests.
                    </span>
                    <MaterialSelectedBadge v-else v-for="interest in personPreferences.interest_skills"
                        :key="interest.id" :label="interest.name" color="info" />
                </div>

                <h6 class="mt-4">Preferred Activities:</h6>
                <div v-if="personPreferences.activity_preferences?.interested_in_any_activity">
                    Interested in any activity.
                </div>
                <div
                    v-else-if="!personPreferences.activity_preferences?.preferred_activities || personPreferences.activity_preferences.preferred_activities.length === 0">
                    No preferred activities.
                </div>
                <div v-else class="d-flex flex-wrap">
                    <MaterialSelectedBadge
                        v-for="activity in personPreferences.activity_preferences.preferred_activities"
                        :key="activity.id" :label="activity.name" color="info" />
                </div>

                <h6 class="mt-4">Preferred Workgroups:</h6>
                <div v-if="personPreferences.workgroup_preferences?.interested_in_any_workgroup">
                    Interested in any workgroup.
                </div>
                <div
                    v-else-if="!personPreferences.workgroup_preferences?.preferred_workgroups || personPreferences.workgroup_preferences.preferred_workgroups.length === 0">
                    No preferred workgroups.
                </div>
                <div v-else class="d-flex flex-wrap">
                    <MaterialSelectedBadge
                        v-for="workgroup in personPreferences.workgroup_preferences.preferred_workgroups"
                        :key="workgroup.id" :label="workgroup.name" color="info" />
                </div>

                <div class="mt-4">
                    <h6>Availability:</h6>
                    <MaterialSelectedBadge :label="personPreferences.availability?.status" color="success" />
                </div>

                <h6 class="mt-4">Rules:</h6>
                <div v-if="!personPreferences.availability?.rules || personPreferences.availability.rules.length === 0">
                    No rules.
                </div>
                <ul v-else>
                    <li v-for="(rule, i) in personPreferences.availability.rules" :key="i">
                        {{ rule.description }}
                    </li>
                </ul>

            </div>
            <div v-else>
                <p>No preferences found.</p>
            </div>
        </template>
    </ContentModal> -->
    <ContentModal v-model="showModal" title="User Preferences">
        <template #default>
            <div v-if="personPreferences">

                <div class="d-flex gap-8">
                    <span class="d-flex gap-2">
                        <h6 class="mb-0">Name:</h6>
                        <p class="mb-0">{{ personPreferences.user_info?.name }}</p>
                    </span>
                    <span class="d-flex gap-2">
                        <h6 class="mb-0">Email:</h6>
                        <p class="mb-0">{{ personPreferences.user_info?.email }}</p>
                    </span>
                </div>

                <h6 class="mt-4">Skills:</h6>
                <div v-if="!personPreferences.skills || personPreferences.skills.length === 0" class="text-muted">
                    No skills.
                </div>
                <div v-else class="d-flex flex-wrap gap-2">
                    <MaterialBadge v-for="skill in personPreferences.skills" :key="skill.id" color="info">
                        {{ skill.name }} (Level {{ skill.level }})
                    </MaterialBadge>
                </div>

                <h6 class="mt-4">Interests:</h6>
                <div v-if="!personPreferences.interest_skills || personPreferences.interest_skills.length === 0" class="text-muted">
                    No interests.
                </div>
                <div v-else class="d-flex flex-wrap gap-2">
                    <MaterialBadge v-for="interest in personPreferences.interest_skills" :key="interest.id" color="info">
                        {{ interest.name }}
                    </MaterialBadge>
                </div>

                <h6 class="mt-4">Preferred Activities:</h6>
                <div v-if="personPreferences.activity_preferences?.interested_in_any_activity" class="text-muted">
                    Interested in any activity.
                </div>
                <div v-else-if="!personPreferences.activity_preferences?.preferred_activities || personPreferences.activity_preferences.preferred_activities.length === 0" class="text-muted">
                    No preferred activities.
                </div>
                <div v-else class="d-flex flex-wrap gap-2">
                    <MaterialBadge v-for="activity in personPreferences.activity_preferences.preferred_activities" :key="activity.id" color="info">
                        {{ activity.name }}
                    </MaterialBadge>
                </div>

                <h6 class="mt-4">Preferred Workgroups:</h6>
                <div v-if="personPreferences.workgroup_preferences?.interested_in_any_workgroup" class="text-muted">
                    Interested in any workgroup.
                </div>
                <div v-else-if="!personPreferences.workgroup_preferences?.preferred_workgroups || personPreferences.workgroup_preferences.preferred_workgroups.length === 0" class="text-muted">
                    No preferred workgroups.
                </div>
                <div v-else class="d-flex flex-wrap gap-2">
                    <MaterialBadge v-for="workgroup in personPreferences.workgroup_preferences.preferred_workgroups" :key="workgroup.id" color="info">
                        {{ workgroup.name }}
                    </MaterialBadge>
                </div>

                <h6 class="mt-4">Availability:</h6>
                <div v-if="Array.isArray(personPreferences.availability) && personPreferences.availability.length > 0">
                    <ul>
                        <li v-for="(item, i) in personPreferences.availability" :key="'av'+i">{{ item }}</li>
                    </ul>
                </div>
                <div v-else-if="personPreferences.availability?.status" class="mb-2">
                    <MaterialBadge color="success">{{ personPreferences.availability.status }}</MaterialBadge>
                </div>
                <div v-else class="text-muted">No availability records.</div>

                <h6 class="mt-4">Scheduling Rules:</h6>
                <div v-if="(!personPreferences.scheduling_rules || personPreferences.scheduling_rules.length === 0) && (!personPreferences.availability?.rules || personPreferences.availability.rules?.length === 0)" class="text-muted">
                    No scheduling rules.
                </div>
                <ul v-else>
                    <li v-for="(rule, i) in (personPreferences.scheduling_rules || [])" :key="'sr'+i">
                        {{ rule.description || rule.rule_description || rule.label || '-' }}
                    </li>
                    <li v-for="(rule, i) in (personPreferences.availability?.rules || [])" :key="'ar'+i">
                        {{ rule.description || rule.rule_description || '-' }}
                    </li>
                </ul>
            </div>
            <div v-else>
                <p>No preferences found.</p>
            </div>
        </template>
    </ContentModal>
    <!-- <Modal v-if="isAssignmentsModalOpen" :modalHeading="assignmentsModalHeading" :iframeUrl="assignmentsIframeUrl" size="xl"
        refresh-on-close @close="closeAssignmentsModal" /> -->
</template>

<script setup>
import { computed, onMounted, reactive, ref, inject } from "vue"
import { useToast } from "vue-toast-notification";
import { useRoute } from "vue-router"
import MaterialInput from '@/components/common/MaterialInput.vue'
import MaterialButton from '@/components/common/MaterialButton.vue'
import MaterialSelect from '@/components/common/MaterialSelect.vue'
import MaterialBadge from '@/components/common/MaterialBadge.vue'
import ContentModal from "../../../components/Modal/ContentModal.vue"
import HelpTextTooltip from '@/components/common/HelpTextTooltip.vue'
import store from "../../../store"
import { getLocationId } from "../../../utils/SessionDataHandler";
import CardLoader from "../../../components/Loader/CardLoader.vue";
import OverlayLoader from "../../../components/Loader/OverlayLoader.vue";
import { isValidEmail } from "../../../utils/validation";
import { usePermissions } from "@/composables/usePermissions";
import api from "@/config/api";
import { toGetcalUrl } from "@/utils/IcalUrlHelper";
import AccountDeletion from "./AccountDeletion.vue";
// import Modal from "../../../components/Modal/Modal.vue";

const props = defineProps({
    personId: {
        type: [Number, String],
        default: null
    },
    locationId: {
        type: [Number, String],
        default: null
    },
    selfProfile: {
        type: Boolean,
        default: false
    },
});
const emit = defineEmits(["account-action-completed"]);

const showModal = ref(false)
const loading = ref(false)
const loadingPreferences = ref(false)
const isSaving = ref(false)
const baseline = ref(null)

const hasChanges = computed(() => {
    if (!baseline.value) return false
    return JSON.stringify(baseline.value) !== JSON.stringify({ ...user })
})
const personPreferences = ref(null);
const emailError = ref(null);
const proxyEmailError = ref(null);
// const isAssignmentsModalOpen = ref(false);
// const assignmentsModalHeading = ref("");
// const assignmentsIframeUrl = ref("");
const toast = useToast()
const route = useRoute()
const {
    isSuperAdmin: permIsSuperAdmin,
    isOrganizationAdmin: permIsOrganizationAdmin,
    canModifyPassword,
    canModifyUserProfile,
} = usePermissions();
const _injectedPersonId = inject('editUserPersonId', null);
const personId = props.personId || _injectedPersonId?.value || Number(route.params.userid);
const locationId = props.locationId || getLocationId()

const user = reactive({
    id: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    useProxyEmail: "",
    proxyEmail: "",
    password: "xxxxxxxx",
    confirmed: "",
    registrationType: "",
    country: "",
    timezone: "",
    mobile: "",
    mobileCarrier: "",
    smsAlerts: "",
    gender: "",
    smsAddress: "",
    lastEmailDate: "",
    lastSignupEmailDate: "",
    lastLogonDate: "",
    icalUrl: "",
    assignments: "",
    sentWelcomeEmail: false,
});
const profileDisplayName = computed(() => `${user.firstName || ""} ${user.lastName || ""}`.trim());

const yesNoOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No" },
]

const registrationTypes = [
    { value: 1, label: "Join an existing Organization" },
    { value: 2, label: "Create a new Organization" },
]

// const countryOptions = [
//     { value: "US", label: "United States" },
//     { value: "PK", label: "Pakistan" },
//     { value: "CA", label: "Canada" },
// ]

// const timezoneOptions = [
//     { value: "America/Chicago", label: "America/Chicago" },
//     { value: "America/New_York", label: "America/New_York" },
//     { value: "Asia/Karachi", label: "Asia/Karachi" },
// ]

const genderOptions = [
    { value: "M", label: "Male" },
    { value: "F", label: "Female" },
]

const countryOptions = computed(() =>
    Object.keys(store.getters.stCountries).map((key) => ({
        value: key,
        label: store.getters.stCountries[key],
    }))
);

const timezoneOptions = computed(() =>
    Object.keys(store.getters.stTimezones).map((key) => ({
        value: key,
        label: store.getters.stTimezones[key],
    }))
);

const isSuperAdmin = computed(() => permIsSuperAdmin.value);
const canResetReminderDate = computed(
    () =>
        permIsSuperAdmin.value ||
        permIsOrganizationAdmin.value ||
        !!(store.getters.stSessionData?.isSuperAdmin || store.getters.stSessionData?.is_super_admin)
);
const resettingReminderDate = ref(false);

// Target user admin flags – populated after fetch in onMounted
const targetUserAdminInfo = ref({});

const passwordPermission = computed(() =>
    canModifyPassword(targetUserAdminInfo.value)
);
const canUpdatePassword = computed(() => passwordPermission.value.canUpdate);

const profilePermission = computed(() =>
    canModifyUserProfile(targetUserAdminInfo.value)
);
const canEditProfile = computed(() => profilePermission.value.canEdit);
const handleSelfAccountActionCompleted = (result) => {
    emit("account-action-completed", result);
};

const resetPersonReminderDate = async () => {
    if (!window.confirm("Reset this person's volunteer reminder date? This makes them eligible for the next reminder; it does not send an email immediately.")) return;

    resettingReminderDate.value = true;
    try {
        const response = await api.post("/reminder-dates/person/reset", {
            location_id: locationId,
            person_id: personId,
        });
        user.lastEmailDate = response.data?.data?.last_alert_date || "2000-01-01 00:00:00";
        toast.success("Volunteer reminder date reset.", { position: "top-right" });
    } catch (error) {
        toast.error(error.response?.data?.message || error.message || "Failed to reset reminder date.", { position: "top-right" });
    } finally {
        resettingReminderDate.value = false;
    }
};

const openModal = async () => {
    loadingPreferences.value = true;
    try {
        const res = await store.dispatch("fetchPersonPreferences", {
            personId,
            locationId,
        });
        // Store action now returns { preferences: {...} } in consistent format
        personPreferences.value = res?.preferences || null;
        showModal.value = true;
    } catch (error) {
        console.error("Error fetching preferences:", error);
        toast.error("Failed to load preferences", { position: "top-right" });
    } finally {
        loadingPreferences.value = false;
    }
}

// const openAssignmentsModal = () => {
//     if (!user.assignments) {
//         toast.warning("Assignments URL not available", {
//             position: "top-right",
//         });
//         return;
//     }
//     const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
//     const label = fullName ? `${fullName} - Assignments` : "Assignments";
//     assignmentsModalHeading.value = label;
//     assignmentsIframeUrl.value = user.assignments;
//     isAssignmentsModalOpen.value = true;
// };

// const closeAssignmentsModal = () => {
//     isAssignmentsModalOpen.value = false;
//     assignmentsModalHeading.value = "";
//     assignmentsIframeUrl.value = "";
// };

const validateEmail = () => {
    if (user.email && user.email.trim() !== '') {
        if (!isValidEmail(user.email)) {
            emailError.value = 'Please enter a valid email address';
        } else {
            emailError.value = null;
        }
    } else {
        emailError.value = null;
    }
};

const validateProxyEmail = () => {
    if (user.proxyEmail && user.proxyEmail.trim() !== '') {
        if (!isValidEmail(user.proxyEmail)) {
            proxyEmailError.value = 'Please enter a valid email address';
        } else {
            proxyEmailError.value = null;
        }
    } else {
        proxyEmailError.value = null;
    }
};

const copyIcalUrl = async () => {
    if (!user.icalUrl) {
        toast.error("No iCal URL available to copy", { position: "top-right" });
        return;
    }

    try {
        await navigator.clipboard.writeText(user.icalUrl);
        toast.success("iCal URL copied to clipboard!", { position: "top-right" });
    } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = user.icalUrl;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand("copy");
            toast.success("iCal URL copied to clipboard!", { position: "top-right" });
        } catch (fallbackErr) {
            toast.error("Failed to copy iCal URL", { position: "top-right" });
        }
        document.body.removeChild(textArea);
    }
}

async function saveUser() {
    // Validate email fields before saving
    validateEmail();
    validateProxyEmail();
    
    if (emailError.value || proxyEmailError.value) {
        toast.error("Please fix validation errors before saving", { position: "top-right" });
        return;
    }
    
    isSaving.value = true;
    try {
        const payload = {
            "cmd": "savepersondetail",
            personid: personId,
            firstname: user.firstName,
            middlename: user.middleName,
            lastname: user.lastName,
            email: user.email,
            proxyemail: user.proxyEmail,
            useproxyemail: user.useProxyEmail,
            mobile: user.mobile,
            password: "",
            enablesms: user.smsAlerts,
            gender: user.gender,
            mobilecarrierid: 1,
            timezone: user.timezone || "UTC",
            country: user.country,
        };

        // Include password if admin has permission and password has been changed
        if (canUpdatePassword.value && user.password && user.password !== "xxxxxxxx" && user.password.trim() !== "") {
            payload.password = user.password;
        }

        // Only include superadmin-only fields if user is superadmin
        if (isSuperAdmin.value) {
            payload.confirmed = user.confirmed;
            payload.registrationtype = user.registrationType;
        }

        const response = await store.dispatch("updateUserDetails", payload);

        if (response) {
            baseline.value = { ...user }
            toast.success(response.message, { position: "top-right" })
            // Reset password field after successful save
            if (canUpdatePassword.value && user.password && user.password !== "xxxxxxxx") {
                user.password = "xxxxxxxx";
            }
        }
    }
    finally {
        isSaving.value = false;
    }
}

onMounted(async () => {
    loading.value = true;
    try {
        const details = await store.dispatch("fetchUserDetails", {
            locationId,
            personId,
        });

        const userData = details.output?.[0];
        if (!userData) return;

        // Store target user admin flags for password permission checks
        targetUserAdminInfo.value = {
            is_organization_admin: !!userData.is_organization_admin,
            is_location_admin: !!userData.is_location_admin,
            is_activity_admin: !!userData.is_activity_admin,
            skill_ids: userData.skill_ids || [],
        };

        await store.dispatch("getCountries");
        await store.dispatch("getTimezones");

        // Fetch assignments URL from manage users list (location-admin-only endpoint)
        let assignmentsUrl = "";
        const sessionData = store.getters.stSessionData || {};
        const isLocationAdmin = sessionData.isSuperAdmin || sessionData.isAdminInLocation;
        if (isLocationAdmin) {
            try {
                await store.dispatch("fetchManageUsers", {
                    locationId,
                    search: "",
                    page: 1,
                    pageSize: 1000,
                });
                const manageUsers = store.getters.allManageUsers || [];
                const foundUser = manageUsers.find(u => u.id === personId);
                if (foundUser && foundUser.assignments) {
                    assignmentsUrl = foundUser.assignments;
                }
            } catch (error) {
                console.warn("Failed to fetch assignments URL:", error);
            }
        }

        Object.assign(user, {
            id: userData.personId,
            firstName: userData.firstName,
            middleName: userData.middleName || "",
            lastName: userData.lastName,
            email: userData.emailAddress,
            useProxyEmail: userData.useproxyemail,
            proxyEmail: userData.proxyemail || "",
            password: "xxxxxxxx",
            confirmed: userData.RegistrationConfirmed,
            registrationType: userData.RegistrationType,
            country: userData.country,
            timezone: userData.time_zone,
            mobile: userData.mobilephone,
            mobileCarrier: "",
            smsAlerts: userData.sms_enabled,
            gender: userData.gender,
            smsAddress: userData.smsAddress || "",
            lastEmailDate: userData.lastEmailDate,
            lastSignupEmailDate: userData.lastSignupEmailDate,
            lastLogonDate: userData.LastLogonDTM,
            icalUrl: toGetcalUrl(userData.iCalUrl) || userData.iCalUrl,
            assignments: assignmentsUrl || userData.assignments || "",
            sentWelcomeEmail: !!userData.sent_welcome_email,
        });
        baseline.value = { ...user }
    }
    finally {
        loading.value = false;
    }
});

</script>

<style scoped>
.form-label {
    color: #7b809a;
}

.d-flex {
    display: flex;
}

.align-items-center {
    align-items: center;
}

.gap-2 {
    gap: 0.5rem;
}
</style>